import { Injectable } from '@angular/core';
import { ExamineeService } from './examinee.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { FaceDetector, FilesetResolver } from '@mediapipe/tasks-vision';

@Injectable({
  providedIn: 'root'
})
export class StreamService {
  private recordedBlobs: Blob[] = [];
  private queueRecordedBlobs: Blob[] = [];
	private chunkIndex = 0;

  isRecording = false;
  selectedCamera: string | undefined = undefined;
  selectedMicrophone: string | undefined = undefined;

	faceDetector!: FaceDetector;
	faceDetectionInterval: any;
	detect_face = false;
	light = false;
	frameRate = 0;

	totalSlices = 0;
  uploadedSlices = 0;
  remainingSlices = 0;

	examinee_id!: number;

	private _passConditions$ = new BehaviorSubject<boolean>(false);
	uploadProgressObs: Subject<{ progress: number, finishUploading: boolean}> = new Subject<{ progress: number, finishUploading: boolean}>();

	constructor(private examineeService: ExamineeService) {}

	async storeChunk(chunk: Blob) {
		this.recordedBlobs.push(chunk);
		this.queueRecordedBlobs.push(chunk);
  }

	async stopStreaming() {
		clearInterval(this.faceDetectionInterval);
	}

	async processUploadQueue() {
		if (this.queueRecordedBlobs.length > 0) {
			this.totalSlices = this.recordedBlobs.length;
			const slice = this.queueRecordedBlobs.shift();

			if (slice) {
				const file_name = this.examineeService.getChunkUploadPath(this.chunkIndex);

				if (!file_name) {
          console.error("Could not generate file name, stopping upload.");
        }

				const signedUrl = await this.getSignedUrl(file_name, slice.type);

				try {
					await this.uploadChunk(slice, signedUrl);
					this.uploadedSlices++;
					this.chunkIndex++;
					this.uploadProgressObs.next({ progress: this.getUploadProgress(), finishUploading: false });
				} catch (error) {
					this.queueRecordedBlobs.unshift(slice);
				}
			}

			this.processUploadQueue();
		} else {
			if (!this.isRecording) {
				this.uploadProgressObs.next({ progress: 100, finishUploading: true });
				this.examineeService.mergeChunks({ frameRate: this.frameRate }).subscribe({
					next: () => { return; },
					error: () => { return; }
				});
      } else {
				setTimeout(() => { this.processUploadQueue(); }, 5000);
			}
		}
	}

	getUploadProgress(): number {
    if (this.totalSlices === 0) return 0; // Avoid division by zero
    return (this.uploadedSlices / this.totalSlices) * 100;
  }

	async getSignedUrl(file_name: string, file_type: string) {
		const maxRetries = 6; // Maximum number of retries
		let retryCount = 0;
		const retryDelay = 10000; // 10 seconds in milliseconds
	
		while (retryCount < maxRetries) {
			try {
				const signedUrl = await this.examineeService.getUploadURL(file_name, file_type);
				return signedUrl
			} catch (error) {
				console.error('Upload error:', error);
			}
	
			retryCount++;
	
			if (retryCount < maxRetries) {
				console.warn(`Retrying upload attempt ${retryCount} in 10 seconds...`);
				await new Promise(resolve => setTimeout(resolve, retryDelay));
			}
		}
	
		this.examineeService.toastErrorMessage('There was a connection failure!', 'Connection Failed');
		// TODO what will do if there was a connection error while recording!
		console.error('Upload failed after maximum retries.');
	}

	async uploadChunk(slice: Blob, signedUrl: string) {
		const maxRetries = 7; // Maximum number of retries
		let retryCount = 0;
		const retryDelay = 5000; // 5 seconds in milliseconds
	
		while (retryCount < maxRetries) {
			try {
				const response = await fetch(signedUrl, {
					method: 'PUT',
					headers: {
						'Content-Type': slice.type,
					},
					body: slice,
				});
	
				if (response.ok) {
					return; 
				} else {
					console.error('Upload error:', response.statusText);
				}
			} catch (error) {
				console.error('Upload error:', error);
			}
	
			retryCount++;
	
			if (retryCount < maxRetries) {
				console.warn(`Retrying upload attempt ${retryCount} in 5 seconds...`);
				await new Promise(resolve => setTimeout(resolve, retryDelay));
			}
		}
	
		this.examineeService.toastErrorMessage('There was a connection failure!', 'Connection Failed');
		this.examineeService.logout();
		console.error('Upload failed after maximum retries.');
	}

	get passConditions$() { return this._passConditions$.asObservable(); }

	async checkConditions(videoPlayer: HTMLVideoElement) {
		const vision = await FilesetResolver.forVisionTasks("https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision/wasm");
		this.faceDetector = await FaceDetector.createFromOptions(
			vision,
			{
				baseOptions: {
					modelAssetPath: "https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite"
				},
				runningMode: "VIDEO",
				minDetectionConfidence: 0.7
			}
		);

		this.faceDetectionInterval = setInterval(() => {
			if (this.faceDetector) {
				const results = this.faceDetector.detectForVideo(videoPlayer, Date.now());
				if (results.detections.length === 1) {
					this.detect_face = true;
				} else {
					this.detect_face = false;
				}
			}

			this.light = this.updateLightQuality(videoPlayer);
			this._passConditions$.next(this.detect_face && this.light)
		}, 1000);
	}

	getLuminance(imageData: ImageData): number {
    let totalLuminance = 0;
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        totalLuminance += 0.2126 * r + 0.7152 * g + 0.0722 * b;
    }

    return totalLuminance / (imageData.width * imageData.height);
	}

	updateLightQuality(videoPlayer: HTMLVideoElement) {
		const videoFrame = videoPlayer;
		if (videoFrame && videoFrame.videoWidth > 0 && videoFrame.videoHeight > 0) { // Check if video is loaded
			const canvas = document.createElement('canvas');
			canvas.width = videoFrame.videoWidth;
			canvas.height = videoFrame.videoHeight;
	
			const context = canvas.getContext('2d');
	
			if (context) { // Check if context is not null
				context.drawImage(videoFrame, 0, 0);
	
				const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
				const luminance = this.getLuminance(imageData);
	
				const minLuminance = 100; // Adjust based on your requirements
				const maxLuminance = 200; // Adjust based on your requirements
	
				return (luminance >= minLuminance && luminance <= maxLuminance);
			} else {
				console.error('2D context is not available.');
			}
		} else {
			setTimeout(() => {
				this.updateLightQuality(videoPlayer);
			}, 1000);
		}
		return false;
	}	
}